import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { switchMap, take } from 'rxjs';
import {
  ActionModalComponent,
  AppRoutes,
  GameDetailsModel,
  GamesRoutes,
  MODAL_TOKEN,
  ModalProvider,
  NavigateDirective,
  playerLeaveReasonMapper
} from '@core';
import { Router } from '@angular/router';
import { GameApplicationsService } from '@games/infra';
import { RemoveApplicationCommandHandler, RemoveApplicationCommandPayload } from '../../application/handlers';
import { LeaveGameReasonComponent } from '../leave-game-reason/leave-game-reason.component';
import { PlayerLeaveReason, UserParticipationStatus } from '@core-http';
import { OrIconComponent, SingleRadioItemModel } from '@ui-components';

const toLeaveReasonItem = (value: PlayerLeaveReason): SingleRadioItemModel<PlayerLeaveReason> => ({
  value,
  name: playerLeaveReasonMapper[value]
});

@Component({
  selector: 'lib-my-hosted-game',
  templateUrl: './manage-my-game.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NavigateDirective, OrIconComponent],
  providers: [RemoveApplicationCommandHandler, GameApplicationsService]
})
export class ManageMyGameComponent {
  @Input() game!: GameDetailsModel;

  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly removeApplicationCommandHandler: RemoveApplicationCommandHandler = inject(
    RemoveApplicationCommandHandler
  );

  private readonly leaveReasons: SingleRadioItemModel<PlayerLeaveReason>[] = [
    PlayerLeaveReason.PLANS_CHANGED,
    PlayerLeaveReason.SCHEDULE_CONFLICT,
    PlayerLeaveReason.CANNOT_ARRIVE_ON_TIME,
    PlayerLeaveReason.INJURY,
    PlayerLeaveReason.PREFER_NOT_TO_SAY
  ].map(toLeaveReasonItem);

  private readonly leaveBeforeStartReasons: SingleRadioItemModel<PlayerLeaveReason>[] = [
    PlayerLeaveReason.PLANS_CHANGED,
    PlayerLeaveReason.CANNOT_ARRIVE_ON_TIME,
    PlayerLeaveReason.INJURY,
    PlayerLeaveReason.PREFER_NOT_TO_SAY
  ].map(toLeaveReasonItem);

  leaveGameModal(): void {
    if (this.game.gameParticipationStatus !== UserParticipationStatus.Approved) {
      this.showCancelApplicationModal();
      return;
    }

    if (this.canLeave()) {
      this.showLeaveWithReasonModal();
    } else {
      this.leaveGameWithAlert();
    }
  }

  goToLineups(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigate([`${AppRoutes.GAMES}/${GamesRoutes.LINEUPS}/${this.game.gameId}`]);
  }

  private showCancelApplicationModal(): void {
    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Rezygnacja z gierki',
        message: 'Czy na pewno chcesz zrezygnować z gierki?',
        btnOk: 'Zrezygnuj',
        btnCancel: 'Anuluj',
        action: () => this.leaveGame()
      }
    });
  }

  private showLeaveWithReasonModal(): void {
    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Opuść gierkę?',
        message: 'Organizator zostanie poinformowany, że rezygnujesz z gierki. Wybierz powód:',
        btnOk: 'Opuść gierkę',
        btnCancel: 'Anuluj',
        template: LeaveGameReasonComponent,
        templateInputs: { reasons: this.leaveReasons },
        requireTemplateSelection: true,
        action: (reason?: unknown) => this.leaveGame(reason as PlayerLeaveReason)
      }
    });
  }

  private leaveGameWithAlert(): void {
    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Opuszczasz gierkę 3 godziny przed jej rozpoczęciem',
        message: '',
        btnOk: 'Opuść gierkę',
        btnCancel: 'Anuluj',
        template: LeaveGameReasonComponent,
        templateInputs: { reasons: this.leaveBeforeStartReasons, withAlert: true },
        requireTemplateSelection: true,
        action: (reason?: unknown) => this.leaveGame(reason as PlayerLeaveReason)
      }
    });
  }

  private leaveGame(leaveReason?: PlayerLeaveReason): void {
    const payload: RemoveApplicationCommandPayload = {
      gameId: this.game.gameId,
      participationStatus: this.game.gameParticipationStatus!,
      leaveReason: leaveReason
    };

    this.removeApplicationCommandHandler
      .remove(payload)
      .pipe(
        take(1),
        switchMap(() => this.router.navigate([AppRoutes.MY_GAMES]))
      )
      .subscribe();
  }

  private canLeave(): boolean {
    if (this.game.gameParticipationStatus === UserParticipationStatus.Applied) return true;

    const now: Date = new Date();
    const gameStart: Date = new Date(this.game.gameDateStart);

    const timeDifference: number = gameStart.getTime() - now.getTime();
    const threeHoursInMilliseconds: number = 3 * 60 * 60 * 1000;

    return timeDifference > threeHoursInMilliseconds;
  }
}
